import * as React from 'react';
import { FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, Skeleton } from '@mui/material';
import { SchemaItem, TrainedModel } from '../@shared/Models/Models';
import { InputBaseSx, SelectMenuItemSx } from '../@shared/templates';
import { TrainedModelProviderInstance } from '../@shared/services/TrainedModelProvider';
import { Palette } from '../@shared/palette';

type ModelDependencySelectProps = {
    modelItem: SchemaItem;
    value: string;
    onChange: (schemaItemId: string, refModelId: string) => void;
};

export const ModelDependencySelect: React.FC<ModelDependencySelectProps> = ({ modelItem, value, onChange }) => {
    const [models, setModels] = React.useState<TrainedModel[]>([]);
    const [isLoading, setIsLoading] = React.useState(true);

    React.useEffect(() => {
        if (!modelItem.refSchemaId) {
            setIsLoading(false);
            return;
        }

        let cancelled = false;
        setIsLoading(true);

        TrainedModelProviderInstance.getAll(modelItem.refSchemaId)
            .then((response: any) => {
                if (cancelled) return;
                const data = response.status === 200 ? response.data : [];
                setModels(Array.isArray(data) ? data : []);
            })
            .catch(() => {
                if (!cancelled) setModels([]);
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });

        return () => { cancelled = true; };
    }, [modelItem.refSchemaId]);

    if (isLoading) {
        return <Skeleton variant="rectangular" height={40} sx={{ borderRadius: '4px', marginBottom: '10px' }} />;
    }

    const handleChange = (e: SelectChangeEvent<string>) => {
        onChange(modelItem.id!, e.target.value);
    };

    return (
        <FormControl size="small" fullWidth sx={{ ...InputBaseSx, marginBottom: '10px' }}>
            <InputLabel id={`model-dependency-label-${modelItem.name}`}>{modelItem.name}</InputLabel>
            <Select
                labelId={`model-dependency-label-${modelItem.name}`}
                id={`model-dependency-${modelItem.name}`}
                value={value}
                label={modelItem.name}
                onChange={handleChange}
                MenuProps={{
                    slotProps: {
                        paper: {
                            sx: {
                                backgroundColor: Palette.backgroundColorPrimary,
                            }
                        }
                    },
                    autoFocus: false,
                }}
                required
            >
                {models.length === 0 && (
                    <MenuItem disabled sx={SelectMenuItemSx}>No models available</MenuItem>
                )}
                {models.map((m) => (
                    <MenuItem key={m.id} value={m.id} sx={SelectMenuItemSx}>
                        {m.name}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
};
